import { useState, useEffect, useCallback } from 'react';
import { parseJinja2Variables } from '../../utils/jinja2Utils';

/**
 * Hook for managing execution form data.
 *
 * @param {Object} template - Template object
 * @returns {Object} Form data, handlers, and form management functions
 */
export const useExecutionForm = template => {
  const [executionData, setExecutionData] = useState({
    input_data: {},
    system_prompt: '',
    execution_settings: {
      temperature: 0.7,
      max_tokens: 1000,
      top_p: 1.0,
      frequency_penalty: 0.0,
      presence_penalty: 0.0,
    },
    output_format: 'text',
  });

  const [showSettings, setShowSettings] = useState(false);

  // Parse template variables from template content
  // Uses the shared utility that includes filtering for false positives
  const parseTemplateVariables = useCallback(templateContent => {
    try {
      if (!templateContent || typeof templateContent !== 'string') return [];
      // Use the shared utility that includes validation and filtering
      return parseJinja2Variables(templateContent);
    } catch (error) {
      // Error parsing template variables
      return [];
    }
  }, []);

  // Initialize input data based on template variables
  const initializeInputData = useCallback(templateVariables => {
    try {
      if (!Array.isArray(templateVariables)) return;

      const initialInputData = {};
      templateVariables.forEach(variable => {
        if (variable && typeof variable === 'string') {
          initialInputData[variable] = '';
        }
      });

      // Ensure all values are strings
      Object.keys(initialInputData).forEach(key => {
        if (typeof initialInputData[key] === 'object') {
          initialInputData[key] = String(initialInputData[key]);
        }
      });

      setExecutionData(prev => ({
        ...prev,
        input_data: initialInputData,
      }));
    } catch (error) {
      // Error initializing input data
      setExecutionData(prev => ({
        ...prev,
        input_data: {},
      }));
    }
  }, []);

  // Handle input data changes
  const handleInputDataChange = useCallback((key, value) => {
    const safeValue = typeof value === 'object' ? String(value) : value;

    setExecutionData(prev => ({
      ...prev,
      input_data: {
        ...prev.input_data,
        [key]: safeValue,
      },
    }));
  }, []);

  // Add new input field
  const addInputField = useCallback(() => {
    setExecutionData(prev => {
      const currentInputData =
        prev.input_data && typeof prev.input_data === 'object'
          ? prev.input_data
          : {};

      return {
        ...prev,
        input_data: {
          ...currentInputData,
          [`custom_param_${Object.keys(currentInputData).length + 1}`]: '',
        },
      };
    });
  }, []);

  // Remove input field
  const removeInputField = useCallback(key => {
    setExecutionData(prev => {
      const currentInputData =
        prev.input_data && typeof prev.input_data === 'object'
          ? prev.input_data
          : {};
      const newInputData = { ...currentInputData };
      delete newInputData[key];
      return {
        ...prev,
        input_data: newInputData,
      };
    });
  }, []);

  // Handle execution settings changes
  const handleExecutionSettingChange = useCallback((key, value) => {
    setExecutionData(prev => ({
      ...prev,
      execution_settings: {
        ...prev.execution_settings,
        [key]: value,
      },
    }));
  }, []);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    if (template?.template) {
      try {
        const templateVariables = parseTemplateVariables(template.template);
        if (Array.isArray(templateVariables)) {
          const initialInputData = {};
          templateVariables.forEach(variable => {
            if (variable && typeof variable === 'string') {
              initialInputData[variable] = '';
            }
          });

          Object.keys(initialInputData).forEach(key => {
            if (typeof initialInputData[key] === 'object') {
              initialInputData[key] = String(initialInputData[key]);
            }
          });

          setExecutionData(prev => ({
            ...prev,
            input_data: initialInputData,
            system_prompt: '',
          }));
        }
      } catch (error) {
        // Error resetting form
        setExecutionData(prev => ({
          ...prev,
          input_data: {},
          system_prompt: '',
        }));
      }
    }
  }, [template, parseTemplateVariables]);

  // Initialize form when template changes
  useEffect(() => {
    if (template?.template) {
      const templateVariables = parseTemplateVariables(template.template);
      initializeInputData(templateVariables);

      // Initialize execution data with template defaults
      setExecutionData(prev => {
        const updates = { ...prev };

        // Initialize output_format from template (independent of model_config)
        if (template.output_format) {
          updates.output_format = template.output_format;
        }

        // Initialize execution settings from model_config if available
        if (template.model_config) {
          updates.execution_settings = {
            temperature: template.model_config.temperature || 0.7,
            max_tokens: template.model_config.max_tokens || 1000,
            top_p: template.model_config.top_p || 1.0,
            frequency_penalty: template.model_config.frequency_penalty || 0.0,
            presence_penalty: template.model_config.presence_penalty || 0.0,
          };
        }

        return updates;
      });
    }
  }, [template, parseTemplateVariables, initializeInputData]);

  // Ensure input_data is always safe
  useEffect(() => {
    if (
      executionData.input_data &&
      typeof executionData.input_data === 'object'
    ) {
      const hasObjectValues = Object.values(executionData.input_data).some(
        value => typeof value === 'object'
      );
      if (hasObjectValues) {
        // Found object values in input_data, cleaning up
        const cleanInputData = {};
        Object.entries(executionData.input_data).forEach(([key, value]) => {
          if (key && typeof key === 'string') {
            cleanInputData[key] =
              typeof value === 'object' ? String(value) : value;
          }
        });
        setExecutionData(prev => ({
          ...prev,
          input_data: cleanInputData,
        }));
      }
    }
  }, [executionData.input_data]);

  return {
    executionData,
    setExecutionData,
    showSettings,
    setShowSettings,
    parseTemplateVariables,
    handleInputDataChange,
    addInputField,
    removeInputField,
    handleExecutionSettingChange,
    resetForm,
  };
};
